<template>
  <div>
    <SwitchGroup as="div" class="flex items-center gap-1">
      <div class="tooltip-container flex items-center gap-1">
        <SwitchLabel as="span" class="flex gap-1 cursor-pointer">
          <span class="text-sm font-medium text-gray-900">{{
            $t("Consolidation des appels multi-files")
          }}</span>
        </SwitchLabel>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-blue-400 hover:text-blue-600 cursor-help transition-colors duration-150"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" x2="12.01" y1="17" y2="17" />
        </svg>

        <div class="tooltip-content">
          <div class="tooltip-arrow"></div>
          <div
            class="bg-gray-700 text-white p-4 rounded-lg shadow-2xl border border-gray-600"
          >
            <h3 class="text-sm font-bold mb-2">
              Comment les appels sont comptés :
            </h3>
            <ul class="text-xs list-disc pl-4 space-y-2">
              <li>
                <span class="font-semibold">DÉSACTIVÉE (par défaut) :</span>
                Chaque appel est compté dans
                <span class="font-bold"
                  >chacune des files d’attente qu’il atteint</span
                >.
                <br />
                <span class="">
                  Dit autrement : chaque appel est comptabilisé
                  <span class="font-bold">une fois par file</span>.
                </span>
                <br />
                <span class="font-semibold">Cas pratique</span>
                (une file principale et une file de débordement) :
                <br />
                un appel répondu dans la file de débordement sera compté
                <span class="font-bold">deux fois</span> :
                <br />
                - <span class="font-bold">non répondu</span> dans la file
                principale,
                <br />
                - <span class="font-bold">répondu</span> dans la file de
                débordement.
                <br />
                <span class="block mt-2">
                  👉 Ce mode met en avant la
                  <span class="font-bold">performance individuelle</span> de
                  chaque file. C’est la
                  <span class="font-bold">statistique la plus précise</span>
                  pour analyser le
                  <span class="font-bold">fonctionnement détaillé</span> de
                  chaque file d’attente.
                </span>
              </li>

              <li>
                <span class="font-semibold">ACTIVÉE :</span>
                Chaque appel est compté
                <span class="font-bold">une seule fois</span>, même s’il passe
                par plusieurs files d’attente.
                <br />
                <span class="">
                  Dit autrement : chaque appel est comptabilisé
                  <span class="font-bold"
                    >une fois pour l’ensemble des files</span
                  >.
                </span>
                <br />
                <span class="font-semibold">Cas pratique</span>
                (une file principale et une file de débordement) :
                <br />
                un appel répondu dans la file de débordement sera compté
                <span class="font-bold">une seule fois</span>, comme
                <span class="font-bold">répondu</span>.
                <br />
                <span class="block mt-2">
                  👉 Ce mode fournit une
                  <span class="font-bold">vue consolidée</span> du volume réel
                  d’appels entrants et de la
                  <span class="font-bold">performance globale</span> des files
                  sélectionnées. C’est la
                  <span class="font-bold">statistique la plus fidèle</span> pour
                  savoir combien d’appels sont réellement pris en charge.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Switch
        v-model="uniqueCalls"
        :class="[
          uniqueCalls ? 'bg-teal-600' : 'bg-gray-200',
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
        ]"
      >
        <span
          aria-hidden="true"
          :class="[
            uniqueCalls ? 'translate-x-5' : 'translate-x-0',
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          ]"
        />
      </Switch>

      <SwitchLabel as="span" class="flex gap-1"> </SwitchLabel>
    </SwitchGroup>
  </div>
</template>

<script>
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";

export default {
  props: {
    uniqueCalls: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:uniqueCalls"],
  components: {
    Switch,
    SwitchGroup,
    SwitchLabel,
  },
  watch: {
    uniqueCalls(newValue) {
      this.$emit("update:uniqueCalls", newValue);
    },
  },
};
</script>

<style scoped>
.switch-group {
  position: absolute;
  right: 1.3rem;
  top: 23.5rem;
}
.switch-group-no-info {
  position: absolute;
  right: 1.3rem;
  top: 18.5rem;
}

.tooltip-container {
  position: relative;
  /* Ensure the container is inline-block to wrap the content tightly */
  display: inline-flex;
}

.tooltip-content {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  position: absolute;
  z-index: 50; /* High z-index to overlay other elements */
  top: 100%; /* Position below the icon/label */
  left: 50%;
  transform: translateX(-50%);
  width: 350px; /* Adjusted width for readability */
  min-width: 280px;
  margin-top: 10px; /* Spacing */
  pointer-events: none; /* Allows clicks to pass through when hidden */
}

/* Show tooltip on hover or when a child element (like the icon) is focused */
.tooltip-container:hover .tooltip-content,
.tooltip-container:focus-within .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.tooltip-arrow {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #374151; /* Matches the bg-gray-700 color */
}
</style>
